import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { MicrophonePermissionState } from '../helpers/enum';
import { getInspectMicrophoneOptions } from '../helpers/get-inspect-microphone-options';
import { type MicrophoneInspection } from '../helpers/interfaces';
import { isMicrophonePermissionDeniedError } from '../helpers/is-microphone-permission-denied-error';
import { isMicrophoneUnavailableError } from '../helpers/is-microphone-unavailable-error';
import { readTrackMuted } from '../helpers/read-track-muted';

@Injectable({
  providedIn: 'root',
})
export class MicrophonePermissionService {
  /**
   * Checks the current microphone permission state.
   * Returns 'granted' if access was previously allowed, 'denied' if blocked
   * or 'prompt' if the user hasn't been asked yet.
   */
  // eslint-disable-next-line class-methods-use-this
  public async checkMicrophonePermission(): Promise<PermissionState> {
    try {
      const { state } = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return state;
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      return MicrophonePermissionState.prompt;
    }
  }

  /**
   * Opens the selected microphone, reads whether the OS has muted it, then stops the track.
   */
  // eslint-disable-next-line class-methods-use-this
  public async inspectMicrophone(deviceId?: string | null): Promise<MicrophoneInspection> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(getInspectMicrophoneOptions(deviceId));
    } catch (error) {
      if (isMicrophonePermissionDeniedError(error)) {
        throw error;
      }

      // Permission is fine; the device is disabled, unplugged, or gone (NotReadableError / NotFoundError).
      // Treat as muted so we show the system dialog instead of switching to phone.
      if (isMicrophoneUnavailableError(error)) {
        return { granted: true, muted: true };
      }

      throw error;
    }

    try {
      const [track] = stream.getAudioTracks();
      return {
        granted: true,
        muted: await readTrackMuted(track),
      };
    } finally {
      stream.getTracks().forEach((track) => track.stop());
    }
  }

  /**
   * Requests microphone access from the browser.
   * This will trigger the browser's permission dialog if not already granted.
   * Returns true if permission was granted, false otherwise.
   */
  // eslint-disable-next-line class-methods-use-this
  public async requestMicrophonePermission(): Promise<boolean> {
    try {
      const { granted } = await this.inspectMicrophone();
      return granted;
    } catch {
      return false;
    }
  }

  public async isDenied(): Promise<boolean> {
    const permissionState = await this.checkMicrophonePermission();
    return permissionState === MicrophonePermissionState.denied;
  }

  /**
   * Observes the microphone permission.
   * Emits the current state first (granted, denied, or prompt), then emits again
   * if the user changes it in the browser.
   */
  // eslint-disable-next-line class-methods-use-this
  public observeMicrophonePermission(): Observable<PermissionState> {
    return new Observable<PermissionState>((subscriber) => {
      let permissionStatus: PermissionStatus | undefined;
      let isActive = true;
      const emitState = () => {
        if (isActive && permissionStatus) {
          subscriber.next(permissionStatus.state);
        }
      };

      try {
        navigator.permissions
          .query({ name: 'microphone' as PermissionName })
          .then((status) => {
            if (!isActive) {
              return;
            }

            permissionStatus = status;
            emitState();
            permissionStatus.addEventListener('change', emitState);
          })
          .catch(() => {
            if (isActive) {
              subscriber.next(MicrophonePermissionState.prompt);
            }
          });
      } catch {
        subscriber.next(MicrophonePermissionState.prompt);
      }

      return () => {
        isActive = false;
        permissionStatus?.removeEventListener('change', emitState);
      };
    });
  }
}
